import { HttpClient } from '@angular/common/http';
import {
    Component,
    effect,
    inject,
    input,
    OnInit,
    signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ShowPdfDialogComponent } from '@app/shared/components/show-pdf-dialog/show-pdf-dialog.component';
import { IVersion } from '@app/shared/models/version.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PdfService } from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { SelfserviceStore } from '@app/stores/selfserviceStore';
import { MatTooltip } from '@angular/material/tooltip';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-global-footer',
    templateUrl: './global-footer.component.html',
    styleUrls: ['./global-footer.component.scss'],
    imports: [MatTooltip, RouterLink, TranslateModule],
})
export class GlobalFooterComponent implements OnInit {
    private selfserviceStore = inject(SelfserviceStore);
    versionString = signal('');
    footerVisibility = signal(true);
    footerVisibilityInput = input<boolean>(undefined, {
        alias: 'footerVisibility',
    });
    isSelfService = input<boolean>(false);

    footerLinks = [
        {
            translationKey: 'GENERAL.IMPRINT',
            link: 'https://www.t-systems.com/de/de/impressum',
        },
        {
            translationKey: 'GENERAL.PRIVACY_POLICY',
            link: 'https://www.t-systems.com/de/de/datenschutz',
        },
        {
            translationKey: 'GENERAL.T',
            link: 'https://www.t-systems.com/de/de',
        },
        {
            translationKey: 'GENERAL.GITHUB',
            link: 'https://github.com/JR-2022/C4L-Addons/tree/main',
        },
    ];

    selfServiceFooterLinks = [
        {
            translationKey: 'SELFSERVICE.FOOTER.LEGAL',
        },
    ];

    constructor(
        private router: Router,
        private http: HttpClient,
        private spinner: NgxSpinnerService,
        private pdfService: PdfService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {
        effect(() => {
            const value = this.footerVisibilityInput();
            if (value !== undefined) {
                this.footerVisibility.set(value);
            }
        });
    }

    async ngOnInit() {
        this.observeRoutes();
        try {
            const version = await lastValueFrom(
                this.http.get<IVersion>(
                    `assets/version.json?v=${new Date().getTime()}`
                )
            );
            if (version) {
                const buildDate = new Date(version.buildDate);
                const formattedDate =
                    buildDate.toLocaleDateString() +
                    ' ' +
                    buildDate.toLocaleTimeString();

                this.versionString.set(
                    [
                        `Version: ${version.version || 'No version'}`,
                        `Build Date: ${formattedDate}`,
                    ].join('\n')
                );
            }
        } catch (error) {}
    }

    private observeRoutes(): void {
        this.router.events.pipe(untilDestroyed(this)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                const showNoHeader = [
                    '/login',
                    '/login-rate-limit',
                    '/login-callback',
                    '/forgot-password',
                    '/check-email',
                    '/set-new-password',
                    // Die Seite bringt ihren eigenen Unauth-Footer mit; der
                    // globale läge sonst darüber und verdeckte dessen Links.
                    '/external/reset-user-password',
                ];
                this.footerVisibility.set(
                    !showNoHeader.includes(event.urlAfterRedirects)
                );
            }
        });
    }

    /**
     * @param legalNote
     * opens modal dialog with pdf preview
     */
    async viewLegalNote() {
        let blobData: Blob | undefined;
        await this.spinner.show();
        try {
            const organizationSite = this.selfserviceStore.organizationSite();
            const organizationSiteKey = organizationSite?._key;
            if (!organizationSiteKey) {
                throw new Error('Organization site key is not available');
            }

            blobData = await lastValueFrom(
                this.pdfService.getConsigneeSelfCheckinLegalDocument({
                    organizationSiteKey: organizationSiteKey,
                })
            );

            await this.spinner.hide();

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.spinner.hide();
            this.translateService.instant('ERROR.DOWNLOAD-NOTE');
        }
    }
}
